/**
 * Leads list.
 *
 * No state library: the loader fetches, `useLoaderData` renders, and filter
 * state lives in the URL so it is shareable and the back button works.
 * "Load more" appends via keyset cursor rather than virtualising.
 */

import { Form, Link, useLoaderData, useNavigation, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { segment as segmentSchema, type LeadListResult, type Segment } from '@erp/contracts'
import { api } from '../api'
import type { FieldRegistry, SavedView } from '@erp/contracts'
import { usePagedRows } from '../paging'
import { filtersParam } from '../view-state'
import { ViewBar, handleViewIntent } from '../components/view-bar'
import { displayName, formatDate, isOverdue } from '../format'
import { useStatus, useUser, useUsers } from '../status-context'
import { useAssignees } from '../use-assignees'
import { Badge, Button, EmptyState, PageHeader, SearchInput, Spinner, Table, Toolbar } from '../ui'

interface ListData {
  page: LeadListResult
  registry: FieldRegistry
  views: SavedView[]
}

async function fetchPage(request: Request): Promise<LeadListResult> {
  const url = new URL(request.url)
  return api.leads.list({
    search: url.searchParams.get('q') ?? undefined,
    statusId: url.searchParams.getAll('status'),
    ownerId: url.searchParams.getAll('owner'),
    // Drop anything the enum does not recognise rather than sending it on — a
    // hand-edited URL should narrow the list, not 400 it.
    segment: url.searchParams
      .getAll('segment')
      .filter((value): value is Segment => segmentSchema.safeParse(value).success),
    followUpDue: url.searchParams.get('due') === 'true',
    filters: filtersParam(url.searchParams),
    cursor: url.searchParams.get('cursor') ?? undefined,
    sort: 'updatedAt',
    direction: 'desc',
    limit: 50,
  })
}

/** Fetcher-only route: just the page, which is what usePagedRows expects. */
export const pageLoader = ({ request }: LoaderFunctionArgs) => fetchPage(request)

export async function loader({ request }: LoaderFunctionArgs): Promise<ListData> {
  const [page, registry, views] = await Promise.all([
    fetchPage(request),
    api.registry('lead'),
    api.views.forEntity('lead'),
  ])
  return { page, registry, views }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  const result = await handleViewIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

export default function Leads() {
  const { page, registry, views } = useLoaderData() as ListData
  const navigation = useNavigation()
  const [searchParams] = useSearchParams()
  const { rows, cursor, loading, loadMore } = usePagedRows(page, '/crm/leads/page')
  // Who is working each lead: owner comes with the row, assignees are fetched
  // for the visible page in one request.
  const assignees = useAssignees(
    'lead',
    rows.map((lead) => lead.id),
  )

  const dueOnly = searchParams.get('due') === 'true'

  return (
    <>
      <PageHeader
        title="Leads"
        actions={
          <Link to="/crm/leads/new" className="ax-btn ax-btn-primary">
            Create
          </Link>
        }
      />

      <ViewBar entity="lead" registry={registry} views={views} />

      <Toolbar>
        <Form method="get" role="search">
          <SearchInput
            name="q"
            defaultValue={searchParams.get('q') ?? ''}
            placeholder="Search leads"
          />
          {/* The follow-up queue — a range scan, not six status filters. */}
          <label className="ax-checkbox">
            <input type="checkbox" name="due" value="true" defaultChecked={dueOnly} />
            Follow-up due
          </label>
          <Button type="submit">Apply</Button>
        </Form>
        <span className="ax-toolbar-count">
          {page.totalCount} {page.totalCount === 1 ? 'lead' : 'leads'}
        </span>
        {navigation.state === 'loading' ? <Spinner /> : null}
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState
          title={dueOnly ? 'Nothing due — you are caught up' : 'No leads match this view'}
          action={
            <Link to="/crm/leads/new" className="ax-btn ax-btn-primary">
              Create a lead
            </Link>
          }
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Organization</th>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col">Owner</th>
                <th scope="col">Working on it</th>
                <th scope="col">Follow-up</th>
                <th scope="col">Last modified</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead) => (
                <tr
                  key={lead.id}
                  className={isOverdue(lead.nextFollowUpAt) ? 'ax-row-overdue' : undefined}
                >
                  <td>
                    <Link to={`/crm/leads/${lead.id}`}>{displayName(lead)}</Link>
                  </td>
                  <td>{lead.organizationName ?? '—'}</td>
                  <td>{lead.email ?? '—'}</td>
                  <td>
                    <StatusCell statusId={lead.statusId} />
                  </td>
                  <td>
                    <OwnerCell ownerId={lead.ownerId} />
                  </td>
                  <td>
                    <AssigneeCell userIds={assignees.get(lead.id) ?? []} />
                  </td>
                  <td>{lead.nextFollowUpAt ? formatDate(lead.nextFollowUpAt) : '—'}</td>
                  <td>{formatDate(lead.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {cursor ? (
            <div className="ax-list-footer">
              <Button onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}

/**
 * Status labels come from the lookup table, which the layout route loads once
 * and shares by context — cheaper than joining them onto every list row.
 */
function StatusCell({ statusId }: { statusId: string }) {
  const status = useStatus(statusId)
  return <Badge color={status?.color ?? 'gray'}>{status?.name ?? '—'}</Badge>
}

/** Ownership: single, and the answer to "whose lead is this". */
function OwnerCell({ ownerId }: { ownerId: string | null }) {
  const owner = useUser(ownerId)
  if (ownerId === null) return <span className="ax-text-muted">Unassigned</span>
  return <span>{owner?.name ?? 'Unknown'}</span>
}

/**
 * Assignment: many, and the answer to "who is actually working it". Distinct
 * from ownership, which is why both columns are here rather than one.
 */
function AssigneeCell({ userIds }: { userIds: readonly string[] }) {
  const users = useUsers()
  if (userIds.length === 0) return <span className="ax-text-muted">—</span>
  const names = userIds.map((id) => users.find((user) => user.id === id)?.name ?? 'Unknown')
  return <span>{names.join(', ')}</span>
}
